import * as Yup from "yup";
import { toast } from "react-toastify";
import { H2, H3 } from "../../components/Text";
import { Request } from "../../hooks/Request";
import { Circle } from "../../components/Animation";
import { Button } from "../../components/Buttons";
import { requestAuth } from "../../components/services/RequestService";
import { validateArray, validateStatus } from "../../utils/validate";
import { validateType } from "../../utils/constant";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import FormDefault from "../../components/Forms/FormDefault";
import { useDialog } from "../../contexts/DialogContext";
import { DialogConfirmation } from "../../components/Modal";
import TableComplete from "../../components/Tables/TableComplete";
import {
  adjuntarDex,
  readDam,
  readDim,
  readParte,
} from "../../components/services/ReadFile";
import * as XLSX from "xlsx";
import { OrderPreviewModal } from "./OrderPreviewModal";
import { InsuranceApplicationPDF } from "../ManegeProcedure/InsuranceApplicationPDF";
import { FormIncidents } from "../ManegeProcedure/FormIncidents";
import { FormQuotes } from "../ManegeProcedure/FormQuotes";
import { FormTransportContract } from "../ManegeProcedure/FormTransportContract";
import { FormArriboImportacion } from "../ManegeProcedure/FormArriboImportacion";
import { FormNacionalizacion } from "../ManegeProcedure/FormNacionalizacion";

import {
  getInsuranceRule,
  INSURANCE_MATERIAL_UPDATED_EVENT,
  normalizeInsuranceCurrency,
} from "../../utils/insuranceRules";

const INSURANCE_RATE_FIELD = "tasa";
const INSURANCE_EUR_USD_RATE_FIELD = "tipoDeCambioSeguroEurUsd";
const PURCHASE_ORDER_EUR_EXCHANGE_RATE_FIELD = "tipoDeCambioO_CBs/Eur";
const TRAVEL_FROM_FIELD = "travesíaDesde";
const TRAVEL_TO_FIELD = "travesíaHasta";
const TRANSPORT_MODE_FIELD = "medioTransporte";

const TRAVEL_FROM_DROPDOWN = "travesiaDesde";
const TRANSPORT_MODE_DROPDOWN = "mediosDeTransporte";

const FIXED_POLICY_NUMBER = "CM - CBR0595503";
const FIXED_TRAVEL_TO = "COCHABAMBA, BOLIVIA";
const RITEX_TOTAL_TARGET_FIELDS = [
  "totalUsdRitex",
  "montoTotalUsdRitex",
  "montoTotalUsd",
];
const PURCHASE_ORDER_MONEY_FIELDS = new Set([
  "subtotalOrdenDeCompra",
  "totalOrdenDeCompra",
]);

function safeJsonParse(value, fallback = {}) {
  try {
    if (!value) return fallback;
    return typeof value === "string" ? JSON.parse(value) : value;
  } catch {
    return fallback;
  }
}

function isEmptyValue(value) {
  return value === null || value === undefined || value === "";
}

function toPurchaseOrderNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;

  let normalized = String(value).replace(/\s/g, "").trim();
  if (normalized === "-") return 0;

  const numericMatch = normalized.match(/-?\d[\d.,]*/);
  if (!numericMatch) return null;

  normalized = numericMatch[0];
  const hasComma = normalized.includes(",");
  const hasDot = normalized.includes(".");

  if (hasComma && hasDot) {
    normalized =
      normalized.lastIndexOf(",") > normalized.lastIndexOf(".")
        ? normalized.replace(/\./g, "").replace(",", ".")
        : normalized.replace(/,/g, "");
  } else if (hasComma) {
    normalized = /^-?\d{1,3}(,\d{3})+$/.test(normalized)
      ? normalized.replace(/,/g, "")
      : normalized.replace(",", ".");
  } else if (/^-?\d{1,3}(\.\d{3})+$/.test(normalized)) {
    normalized = normalized.replace(/\./g, "");
  }

  const number = Number(normalized);
  return Number.isFinite(number) ? number : null;
}

function toPurchaseOrderExchangeRate(value) {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;

  const raw = String(value).replace(/\s/g, "").trim();
  const numericMatch = raw.match(/-?\d+(?:[.,]\d+)?/);
  if (!numericMatch) return null;

  const number = Number(numericMatch[0].replace(",", "."));
  return Number.isFinite(number) ? number : null;
}

function roundPurchaseOrderMoney(value) {
  return Number(value.toFixed(2));
}

function normalizePurchaseOrderMoneyValue(value) {
  if (isEmptyValue(value)) return value;

  const number = toPurchaseOrderNumber(value);
  return number === null ? value : roundPurchaseOrderMoney(number);
}

function isPurchaseOrderEurCurrency(value) {
  return normalizeInsuranceCurrency(value) === "EUR";
}

function isPurchaseOrderExchangeRateDifferenceLabel(value) {
  const normalized = String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[.,:;_/-]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();

  if (!normalized) return false;

  return (
    normalized.includes("DIF T C") ||
    normalized.includes("DIF TC") ||
    normalized.includes("TIPO DE CAMBIO") ||
    normalized.includes("TIPO CAMBIO") ||
    normalized.includes("DIFERENCIA CAMBIARIA") ||
    normalized.includes("DIFERENCIA DE CAMBIO")
  );
}

function getSuggestedInsuranceRate(dataValues) {
  const items = dataValues?.itemsOrdenDeCompraSubDatas || [];
  const itemWithRate = items.find((item) => {
    const extra = safeJsonParse(item.description2);
    return !isEmptyValue(extra?.insuranceRate);
  });
  if (!itemWithRate) return "";
  const extra = safeJsonParse(itemWithRate.description2);
  return String(extra.insuranceRate);
}

const FillForm = (props) => {
  const navigate = useNavigate();
  const {
    procedure,
    reload,
    onClose,
    rolId,
    step,
    typeId,
    openModal,
    handleSendProcedure,
    rols = [],
  } = props;
  const { openDialog, dialogClose } = useDialog();

  const currentProcedureId = procedure.id || procedure.procedureId;
  const parentProcedureId =
    procedure.fatherProcedureId ||
    procedure.parentProcedureId ||
    procedure.fatherProcedure?.id ||
    procedure.fatherProcedure?.procedureId;

  const { data, loading, call } = Request({
    urlApi: `/processstep/${procedure.processStepId}/procedure/${currentProcedureId}?type=2`,
  });

  const {
    data: insuranceDataValues,
    loading: insuranceLoading,
    call: refetchInsurance,
  } = Request({
    urlApi: `/Procedure/${currentProcedureId}/dataValues?fieldnames=itemsOrdenDeCompra&fieldnames=moneda`,
  });

  const { data: ritexParentData, loading: ritexParentLoading } = Request({
    urlApi:
      Number(step) === 11 && parentProcedureId
        ? `/Procedure/${parentProcedureId}/dataValues?fieldnames=proveedor&fieldnames=descripciónGeneralDeLaCompra`
        : null,
  });

  const { data: ritexCurrentData, loading: ritexCurrentLoading } = Request({
    urlApi:
      Number(step) === 11
        ? `/Procedure/${currentProcedureId}/dataValues?fieldnames=subtotalOrdenDeCompra`
        : null,
  });

  const suggestedInsuranceRate = getSuggestedInsuranceRate(insuranceDataValues);
  const purchaseOrderCurrency = normalizeInsuranceCurrency(
    insuranceDataValues?.moneda || "USD",
  );

  useEffect(() => {
    function handleInsuranceMaterialUpdated(event) {
      const updatedProcedureId = event?.detail?.procedureId;
      if (
        updatedProcedureId &&
        String(updatedProcedureId) !== String(currentProcedureId)
      ) {
        return;
      }
      refetchInsurance();
    }
    window.addEventListener(
      INSURANCE_MATERIAL_UPDATED_EVENT,
      handleInsuranceMaterialUpdated,
    );
    return () => {
      window.removeEventListener(
        INSURANCE_MATERIAL_UPDATED_EVENT,
        handleInsuranceMaterialUpdated,
      );
    };
  }, [currentProcedureId, refetchInsurance]);

  function handleExcelModal(field) {
    navigate(
      `/rol/${rolId}/procedure-type/${typeId}/step/${step}/procedure/${
        currentProcedureId
      }/generate/${field.url}${
        field.type === "reviewExcel"
          ? "/review"
          : field.type === "correctExcel"
            ? "/answer"
            : ""
      }`,
    );
    onClose();
  }

  function dataIntialValues(list) {
    let aux = {};
    list?.forEach((element) => {
      const initialValue =
        element.initial === "false" ? false : element.initial;
      const savedValue = PURCHASE_ORDER_MONEY_FIELDS.has(element.name)
        ? normalizePurchaseOrderMoneyValue(initialValue)
        : initialValue;
      const ritexParentValue =
        Number(step) === 11 && element.name === "proveedor"
          ? ritexParentData?.proveedor
          : Number(step) === 11 &&
              element.name === "descripciónGeneralDeLaCompra"
            ? ritexParentData?.descripciónGeneralDeLaCompra
            : "";
      const ritexSubTotalValue =
        Number(step) === 11 && RITEX_TOTAL_TARGET_FIELDS.includes(element.name)
          ? ritexCurrentData?.subtotalOrdenDeCompra || ""
          : "";

      const value = !isEmptyValue(ritexParentValue)
        ? ritexParentValue
        : !isEmptyValue(ritexSubTotalValue)
          ? ritexSubTotalValue
          : element.name === "polizaNro" && isEmptyValue(savedValue)
            ? FIXED_POLICY_NUMBER
            : element.name === "cobertura" && isEmptyValue(savedValue)
              ? "TODO RIESGO"
              : element.name === TRAVEL_TO_FIELD && isEmptyValue(savedValue)
                ? FIXED_TRAVEL_TO
                : element.name === INSURANCE_RATE_FIELD &&
                    !isEmptyValue(suggestedInsuranceRate)
                  ? suggestedInsuranceRate
                  : savedValue || "";

      aux = {
        ...aux,
        [element.name]: value,
      };
    });
    return aux;
  }

  function dataFields(list) {
    let aux = {};
    list?.forEach((element) => {
      if (element.name === PURCHASE_ORDER_EUR_EXCHANGE_RATE_FIELD) {
        aux = {
          ...aux,
          [element.name]: element.validate
            ? Yup.number()
                .transform((value, originalValue) =>
                  originalValue === "" ||
                  originalValue === null ||
                  originalValue === undefined
                    ? null
                    : value,
                )
                .nullable()
                .typeError("Ingrese un tipo de cambio válido")
                .min(0, "El tipo de cambio no puede ser negativo")
            : null,
        };
        return;
      }

      aux = {
        ...aux,
        [element.name]: element.validate
          ? validateType[element.type] || null
          : null,
      };
    });
    return aux;
  }

  const getFieldId = (fieldName, fallBackId, fieldsArr) => {
    const f = fieldsArr.find(
      (x) => x.name && x.name.toLowerCase() === fieldName.toLowerCase(),
    );
    return f ? f.id : fallBackId;
  };

  const getCellValue = (worksheet, r, c) => {
    const addr = XLSX.utils.encode_cell({ r, c });
    const cell = worksheet[addr];
    return cell ? cell.v : null;
  };

  const getFieldIdByNames = (fieldNames, fallBackId, fieldsArr) => {
    const names = Array.isArray(fieldNames) ? fieldNames : [fieldNames];

    const field = fieldsArr.find(
      (item) =>
        item.name &&
        names.some((name) => item.name.toLowerCase() === name.toLowerCase()),
    );

    return field ? field.id : fallBackId;
  };

  const normalizePurchaseOrderValue = (value) =>
    String(value ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, " ")
      .trim()
      .toUpperCase();

  const getPurchaseOrderItemKey = (item) =>
    [
      item.itemNo,
      item.desc,
      item.unit,
      item.amount,
      item.unitPrice,
      item.discount,
      item.itemTotal,
      item.delivery,
    ]
      .map(normalizePurchaseOrderValue)
      .join("|");

  const getPurchaseOrderCargoKey = (cargo) =>
    [cargo.label, cargo.value].map(normalizePurchaseOrderValue).join("|");

  const pushUniquePurchaseOrderRow = (rows, row, getKey) => {
    const key = getKey(row);

    if (!key) return;

    const exists = rows.some((currentRow) => getKey(currentRow) === key);

    if (!exists) rows.push(row);
  };

  const getSubDataRows = (responseData) => {
    if (Array.isArray(responseData)) return responseData;
    if (Array.isArray(responseData?.data)) return responseData.data;
    if (Array.isArray(responseData?.items)) return responseData.items;
    if (Array.isArray(responseData?.results)) return responseData.results;
    if (Array.isArray(responseData?.records)) return responseData.records;

    return [];
  };

  const deletePurchaseOrderSubDataByField = async (procedureId, fieldId) => {
    const response = await requestAuth(
      "get",
      `/dataSet/procedure/${procedureId}/field/${fieldId}/subdata`,
    );

    const rows = getSubDataRows(response.data);

    await Promise.all(
      rows
        .filter((row) => row?.id)
        .map((row) =>
          requestAuth("delete", `/dataSet/procedure/subData/${row.id}`),
        ),
    );
  };

  async function processPurchaseOrder(
    file,
    procedureId,
    fieldsList,
    formValues = {},
  ) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];

          const ref = worksheet["!ref"];
          const range = XLSX.utils.decode_range(ref);
          const minRow = range.s.r;
          const maxRow = range.e.r;

          const cell = (absRow, absCol) =>
            getCellValue(worksheet, absRow, absCol);

          const purchaseOrderCurrency = normalizeInsuranceCurrency(
            formValues?.moneda || "",
          );
          const exchangeRate = toPurchaseOrderExchangeRate(
            formValues?.[PURCHASE_ORDER_EUR_EXCHANGE_RATE_FIELD],
          );
          const shouldConvertBolivianosToEur =
            purchaseOrderCurrency === "EUR" &&
            exchangeRate !== null &&
            exchangeRate > 0;

          const moneyValue = (value) => {
            const numberValue = toPurchaseOrderNumber(value);

            if (numberValue === null) {
              if (value === null || value === undefined || value === "")
                return 0;
              throw new Error(
                `No se pudo interpretar el monto "${value}" del Excel.`,
              );
            }

            return shouldConvertBolivianosToEur
              ? roundPurchaseOrderMoney(numberValue / exchangeRate)
              : numberValue;
          };

          const serialToDate = (serial) => {
            if (typeof serial !== "number") return "";
            return new Date(Math.round((serial - 25569) * 86400 * 1000))
              .toISOString()
              .split("T")[0];
          };

          const cleanPrefix = (str) => {
            if (!str) return "";
            const idx = str.indexOf(":");
            return idx !== -1 ? str.slice(idx + 1).trim() : str.trim();
          };

          const findNextValue = (keyword) => {
            for (let r = minRow; r <= maxRow; r++) {
              for (let c = range.s.c; c <= range.e.c; c++) {
                const v = cell(r, c);
                if (
                  typeof v === "string" &&
                  v.toUpperCase().includes(keyword.toUpperCase())
                ) {
                  for (let k = c + 1; k <= range.e.c; k++) {
                    const next = cell(r, k);
                    if (next !== null && next !== undefined && next !== "")
                      return next;
                  }
                }
              }
            }
            return "";
          };

          const fechaRaw = findNextValue("FECHA :");
          const fecha =
            typeof fechaRaw === "number"
              ? serialToDate(fechaRaw)
              : String(fechaRaw ?? "");
          const nroOc = findNextValue("Nro. O.C.:");
          const scNo = findNextValue("SC No.");
          const pedido = findNextValue("Pedido :");
          const proveedor = findNextValue("Para :");

          const terminosPagoRaw = cell(26, 3) || "";
          const condicionesRaw = cell(27, 3) || "";
          const observacionesStr = cell(28, 3) || "";

          const terminosPago = cleanPrefix(String(terminosPagoRaw));
          const condiciones = cleanPrefix(String(condicionesRaw));
          const observaciones = cleanPrefix(String(observacionesStr));

          let subtotalAbsRow = -1;
          let totalAbsRow = -1;

          for (let r = minRow; r <= maxRow; r++) {
            const g = cell(r, 6);
            if (typeof g === "string") {
              if (
                g.toUpperCase().includes("SUBTOTAL") &&
                subtotalAbsRow === -1
              ) {
                subtotalAbsRow = r;
              }
              if (
                g.toUpperCase().includes("ORDEN DE COMPRA EN") &&
                subtotalAbsRow !== -1
              ) {
                totalAbsRow = r;
                break;
              }
            }
          }

          const subtotal =
            subtotalAbsRow !== -1
              ? roundPurchaseOrderMoney(moneyValue(cell(subtotalAbsRow, 60)))
              : 0;
          const excelTotal =
            totalAbsRow !== -1
              ? roundPurchaseOrderMoney(moneyValue(cell(totalAbsRow, 60)))
              : 0;

          let difTc = 0;

          const cargoRows = [];
          if (subtotalAbsRow !== -1 && totalAbsRow !== -1) {
            for (let r = subtotalAbsRow + 1; r < totalAbsRow; r++) {
              const label = cell(r, 6);
              const value = cell(r, 60);
              if (label && typeof label === "string" && label.trim() !== "") {
                const cargoValue = moneyValue(value);

                if (isPurchaseOrderExchangeRateDifferenceLabel(label)) {
                  difTc = cargoValue;
                  continue;
                }

                const cargo = { label: label.trim(), value: cargoValue };

                pushUniquePurchaseOrderRow(
                  cargoRows,
                  cargo,
                  getPurchaseOrderCargoKey,
                );
              }
            }
          }

          const cargosTotal = cargoRows.reduce(
            (acc, cargo) => acc + (toPurchaseOrderNumber(cargo.value) ?? 0),
            0,
          );
          const total =
            subtotalAbsRow !== -1
              ? roundPurchaseOrderMoney(subtotal + cargosTotal)
              : excelTotal;

          const items = [];
          for (let r = minRow; r <= maxRow; r++) {
            if (r === subtotalAbsRow) break;

            const itemNo = cell(r, 2);
            if (itemNo === null || itemNo === undefined) continue;
            if (typeof itemNo !== "number" && isNaN(parseFloat(itemNo)))
              continue;

            const desc = cell(r, 6) ? String(cell(r, 6)) : "";
            if (!desc.trim()) continue;
            if (desc.toUpperCase().includes("SUBTOTAL")) continue;
            if (desc.toUpperCase().includes("ORDEN DE COMPRA")) continue;

            const unit = cell(r, 31) ? String(cell(r, 31)) : "";
            const amount = cell(r, 36) ?? 0;
            const unitPrice = moneyValue(cell(r, 44));
            const discount = moneyValue(cell(r, 53));
            const itemTotal = moneyValue(cell(r, 60));
            const delivSer = cell(r, 67);
            const delivery = delivSer ? serialToDate(delivSer) : "";

            pushUniquePurchaseOrderRow(
              items,
              {
                itemNo,
                desc,
                unit,
                amount,
                unitPrice,
                discount,
                itemTotal,
                delivery,
              },
              getPurchaseOrderItemKey,
            );
          }

          const cabeceraPayload = [
            {
              Value: fecha,
              FieldId: getFieldId("fechaDeOrdenDeCompra", 2012, fieldsList),
              name: "fechaDeOrdenDeCompra",
              label: "Fecha de orden de compra",
            },
            {
              Value: nroOc,
              FieldId: getFieldId("nroOc", 2013, fieldsList),
              name: "nroOc",
              label: "Nro de orden de compra",
            },
            {
              Value: scNo,
              FieldId: getFieldId("scNro", 2014, fieldsList),
              name: "scNro",
              label: "Nro de solicitud de orden de compra",
            },
            {
              Value: pedido,
              FieldId: getFieldId("númeroDePedido", 807, fieldsList),
              name: "númeroDePedido",
              label: "Nro de Pedido",
            },
            {
              Value: proveedor,
              FieldId: getFieldId("proveedor", 5, fieldsList),
              name: "proveedor",
              label: "Proveedor",
            },
            {
              Value: terminosPago,
              FieldId: getFieldId("terminosPago", 2015, fieldsList),
              name: "terminosPago",
              label: "Términos de pago",
            },
            {
              Value: condiciones,
              FieldId: getFieldId("condiciones", 2016, fieldsList),
              name: "condiciones",
              label: "INCOTERMS",
            },
            {
              Value: observaciones,
              FieldId: getFieldId("obsDeLaOrdenDeCompra", 2017, fieldsList),
              name: "obsDeLaOrdenDeCompra",
              label: "Observaciones de la orden de compra",
            },
            {
              Value: subtotal || 0,
              FieldId: getFieldId("subtotalOrdenDeCompra", 2018, fieldsList),
              name: "subtotalOrdenDeCompra",
              label: "Subtotal de la orden de compra",
            },
            {
              Value: difTc,
              FieldId: getFieldId("difTc", 2019, fieldsList),
              name: "difTc",
              label: "Diferencia de tipo de cambio",
            },
            {
              Value: total || 0,
              FieldId: getFieldId("totalOrdenDeCompra", 2027, fieldsList),
              name: "totalOrdenDeCompra",
              label: "Total de la orden de compra",
            },
          ]
            .map((p) => ({ ...p, ProcedureId: procedureId }))
            .filter(
              (p) =>
                p.Value !== undefined && p.Value !== null && p.Value !== "",
            );

          openDialog(
            <OrderPreviewModal
              extractedData={{
                cabecera: cabeceraPayload,
                items,
                cargoRows,
                procedureId,
                fieldsList,
                exchangeRateInfo: {
                  currency: purchaseOrderCurrency || formValues?.moneda || "",
                  exchangeRate: exchangeRate ?? 0,
                  convertedFromBolivianos: shouldConvertBolivianosToEur,
                },
              }}
              onClose={() => {
                dialogClose();
                reject(new Error("Previsualización cancelada por el usuario."));
              }}
              onConfirm={async (editedData) => {
                const {
                  cabecera,
                  items,
                  cargoRows,
                  procedureId,
                  fieldsList,
                  insuranceMaterialType,
                } = editedData;

                const insuranceRule = getInsuranceRule(insuranceMaterialType);
                try {
                  await requestAuth("post", "/dataset", cabecera);

                  const itemsFieldId = getFieldIdByNames(
                    ["itemsOrdenDeCompra", "items"],
                    2009,
                    fieldsList,
                  );

                  const additionalsFieldId = getFieldIdByNames(
                    ["adicionalesDeLaOrdenDeCompra"],
                    2028,
                    fieldsList,
                  );

                  await Promise.all([
                    deletePurchaseOrderSubDataByField(
                      procedureId,
                      itemsFieldId,
                    ),
                    deletePurchaseOrderSubDataByField(
                      procedureId,
                      additionalsFieldId,
                    ),
                  ]);

                  let itemsSuccess = 0;
                  for (const item of items) {
                    await requestAuth(
                      "post",
                      `/dataSet/procedure/${procedureId}/field/${itemsFieldId}/subdata`,
                      {
                        Label: String(item.desc),
                        NumberValue: Number(item.itemNo),
                        Description: "",
                        Description2: JSON.stringify({
                          unit: String(item.unit),
                          amount: String(item.amount),
                          desc: String(item.desc),
                          discount: String(item.discount),
                          delivery: String(item.delivery),
                          unitPrice: String(item.unitPrice),
                          itemTotal: String(item.itemTotal || ""),
                          insuranceMaterialType: insuranceRule.value,
                          insuranceFactor: String(insuranceRule.factor),
                          insuranceRate: String(insuranceRule.ratePercent),
                          insuranceLabel: insuranceRule.label,
                          purchaseOrderCurrency,
                          tr4: "",
                          providerCode: "",
                        }),
                        unit: String(item.unit),
                        amount: String(item.amount),
                        desc: String(item.desc),
                        discount: String(item.discount),
                        delivery: String(item.delivery),
                        tr4: "",
                        providerCode: "",
                        isNew: false,
                      },
                    );
                    itemsSuccess++;
                  }

                  for (const cargo of cargoRows) {
                    await requestAuth(
                      "post",
                      `/dataSet/procedure/${procedureId}/field/${additionalsFieldId}/subdata`,
                      {
                        Label: cargo.label,
                        NumberValue: 0,
                        Description: "",
                        Description2: JSON.stringify({
                          unitPrice: String(cargo.value),
                          amount: String(1),
                        }),
                        value: String(cargo.value),
                        isNew: false,
                      },
                    );
                  }

                  console.log(
                    JSON.stringify(
                      {
                        success: true,
                        message: "Orden de compra procesada exitosamente",
                        data: {
                          cabecera: {
                            nro_oc: nroOc,
                            total_items: items.length,
                          },
                          items_procesados: itemsSuccess,
                          cargos_procesados: cargoRows.length,
                        },
                      },
                      null,
                      2,
                    ),
                  );

                  dialogClose();
                  resolve();
                } catch (err) {
                  dialogClose();
                  reject(err);
                }
              }}
            />,
          );
        } catch (err) {
          console.error("Excel proc error", err);
          reject(err);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  async function onSubmit(values, resetForm, fields) {
    const result = await Promise.all(
      fields.map(async (element) => {
        if (element.type === "file" || element.type === "fileRead") {
          if (
            (values[element.name] === "" || values[element.name] === null) &&
            (element.initial === "" || element.initial === null)
          ) {
            return null;
          } else if (
            (values[element.name] !== "" && !element.initial) ||
            (values[element.name] !== "" &&
              values[element.name] !== element.initial)
          ) {
            let formData = new FormData();
            formData.append("Name", element.name);
            formData.append("File", values[element.name]);
            formData.append("originalName", values[element.name].name);
            formData.append("Status", "verified");
            formData.append("ProcedureId", procedure.id);
            const fileContents = await requestAuth(
              "post",
              "/files",
              formData,
            ).then((res) => {
              return {
                Value: res.data.url,
                ProcedureId: procedure.id,
                FieldId: element.id,
                FileId: res.data.id,
                name: element.name,
                url: element.url,
                type: element.type,
              };
            });
            return fileContents;
          } else {
            return {
              Value: element.initial,
              ProcedureId: procedure.id,
              FieldId: element.id,
              name: element.name,
              url: element.url,
              type: element.type,
            };
          }
        } else if (element.type === "selectNewOption") {
          if (values[element.name + "_isNew"]) {
            await handleCreateOption(
              values[element.name] || values[element.name] === 0
                ? values[element.name]
                : "",
              element.url,
            );
          }
          return {
            Value:
              values[element.name] || values[element.name] === 0
                ? values[element.name]
                : "",
            ProcedureId: procedure.id,
            FieldId: element.id,
            name: element.name,
            url: element.url,
            type: element.type,
          };
        } else {
          if (element.name === "requiereTipoDeCambioBs/Eur") return null;

          if (
            element.name === PURCHASE_ORDER_EUR_EXCHANGE_RATE_FIELD &&
            !isPurchaseOrderEurCurrency(values.moneda)
          ) {
            return {
              Value: "",
              ProcedureId: procedure.id,
              FieldId: element.id,
              name: element.name,
              url: element.url,
              type: element.type,
            };
          }

          const fieldValue =
            element.name === INSURANCE_RATE_FIELD &&
            !isEmptyValue(suggestedInsuranceRate)
              ? suggestedInsuranceRate
              : values[element.name] || values[element.name] === 0
                ? values[element.name]
                : "";
          const value = PURCHASE_ORDER_MONEY_FIELDS.has(element.name)
            ? normalizePurchaseOrderMoneyValue(fieldValue)
            : fieldValue;
          return {
            Value: value,
            ProcedureId: procedure.id,
            FieldId: element.id,
            name: element.name,
            url: element.url,
            type: element.type,
          };
        }
      }),
    );
    if (procedure.category === "multiple") {
      await submitMultiDataset(
        result.filter((n) => n),
        resetForm,
        values.btnSecondary,
      );
    } else {
      await submitDataset(
        result.filter((n) => n),
        resetForm,
        values.btnSecondary,
      );
    }

    // EXTRA ADICIONAL LUEGO DE SUBMIT
    const ocField = fields.find((f) => f.name === "ordenDeCompra");
    if (ocField && values["ordenDeCompra"]) {
      const file = values["ordenDeCompra"];
      if (file.name.match(/\.(xlsx|xls)$/)) {
        try {
          toast.info("Leyendo Orden de Compra desde Excel...");
          await processPurchaseOrder(file, procedure.id, fields, values);
          toast.success(
            "Excel procesado y enviado a los servicios correctamente",
          );
        } catch (e) {
          toast.error("Extracción de Excel anulada: " + e.message);
        }
      }
    }
  }

  async function handleCreateOption(value, dropdown) {
    await requestAuth("post", "/dropdown/option", {
      Name: value,
      DropdownListLabel: dropdown,
    });
  }

  async function submitDataset(values, resetForm, btnSecondary) {
    await requestAuth("post", "/dataset", values)
      .then(async () => {
        const listRead = values.filter((f) => f.type === "fileRead");
        if (listRead && listRead.length > 0) {
          await readDataProcedure(
            0,
            { ...listRead, id: listRead.FieldId },
            [],
            resetForm,
            btnSecondary,
          );
        } else {
          reload();
          onClose();
          resetForm();
          if (btnSecondary) {
            await handleSendProcedure(procedure, reload);
          }
          toast.success("Envio exitoso");
          if (Number(step) === 10) {
            openDialog(<InsuranceApplicationPDF procedureId={procedure.id} />);
          }
          if (Number(step) === 19) {
            openDialog(
              <div className="bg-white p-6 rounded-lg text-center max-w-sm mx-auto shadow-xl">
                <h3 className="text-lg font-bold mb-4 text-gray-800">
                  Cierre de Importación
                </h3>
                <p className="mb-6 text-gray-600">
                  ¿Desea registrar alguna incidencia para este trámite?
                </p>
                <div className="flex justify-center gap-4">
                  <button
                    onClick={() => dialogClose()}
                    className="px-5 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition font-medium"
                  >
                    No
                  </button>
                  <button
                    onClick={() => {
                      openDialog(<FormIncidents procedure={procedure} />);
                    }}
                    className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
                  >
                    Sí, registrar
                  </button>
                </div>
              </div>,
            );
          }
        }
      })
      .catch(() => {
        toast.error("Error en el envio");
      });
  }

  async function readDataProcedure(
    index,
    fields,
    result,
    resetForm,
    btnSecondary,
  ) {
    if (index < fields.length) {
      switch (fields[index].url) {
        case "adjuntarDim":
          await readDim(
            fields,
            index,
            procedure,
            result,
            async (i, f, r) =>
              await readDataProcedure(i, f, r, resetForm, btnSecondary),
          );
          break;
        case "dimRegularizada":
          await readDim(
            fields,
            index,
            procedure,
            result,
            async (i, f, r) =>
              await readDataProcedure(i, f, r, resetForm, btnSecondary),
          );
          break;
        case "adjuntarDex":
          await adjuntarDex(
            fields,
            index,
            procedure,
            result,
            async (i, f, r) =>
              await readDataProcedure(i, f, r, resetForm, btnSecondary),
          );
          break;
        case "documentoDam":
          await readDam(
            fields,
            index,
            procedure,
            result,
            async (i, f, r) =>
              await readDataProcedure(i, f, r, resetForm, btnSecondary),
          );
          break;
        case "parteDeRecepcion":
          await readParte(
            fields,
            index,
            procedure,
            result,
            async (i, f, r) =>
              await readDataProcedure(i, f, r, resetForm, btnSecondary),
          );
          break;
      }
    } else {
      const badResult = result.filter(
        (read) => !validateStatus(read.readStatus) && read.validate,
      );
      if (validateArray(badResult)) {
        reload();
        onClose();
        resetForm();
        toast.error(badResult[0].readData);
      } else {
        reload();
        onClose();
        resetForm();
        if (btnSecondary) handleSendProcedure(procedure, reload);
        toast.success("Envio exitoso");
        if (Number(step) === 10) {
          openDialog(<InsuranceApplicationPDF procedureId={procedure.id} />);
        }
        if (Number(step) === 19) {
          openDialog(
            <div className="bg-white p-6 rounded-lg text-center max-w-sm mx-auto shadow-xl">
              <h3 className="text-lg font-bold mb-4 text-gray-800">
                Cierre de Importación
              </h3>
              <p className="mb-6 text-gray-600">
                ¿Desea registrar alguna incidencia para este trámite?
              </p>
              <div className="flex justify-center gap-4">
                <button
                  onClick={() => dialogClose()}
                  className="px-5 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition font-medium"
                >
                  No
                </button>
                <button
                  onClick={() => {
                    openDialog(<FormIncidents procedure={procedure} />);
                  }}
                  className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
                >
                  Sí, registrar
                </button>
              </div>
            </div>,
          );
        }
      }
    }
  }

  async function submitMultiDataset(values, resetForm, btnSecondary) {
    await requestAuth("get", `/procedure/${procedure.id}/sub-procedures`)
      .then((resp) => {
        submitDataset(
          fillSubProcedures(values, resp.data),
          resetForm,
          btnSecondary,
        );
      })
      .catch(() => {
        toast.error("Error en el envio");
      });
  }

  function fillSubProcedures(values, list) {
    let fillProcedure = values;
    list.forEach((element) => {
      const subProcedure = values.map((item) => ({
        ...item,
        ProcedureId: element.id,
      }));
      fillProcedure = [...fillProcedure, ...subProcedure];
    });
    return fillProcedure;
  }

  function handleReaload() {
    call();
    reload();
  }

  function listSubProcedure() {
    openModal(<ListSubProcedure {...props} />);
  }

  if (!loading || !insuranceLoading) {
    return <Circle />;
  }
  return (
    <>
      {validateArray(data.fields) ? (
        <>
          <div className="flex justify-between mb-2">
            <H2>
              Llenar formulario de trámite{" "}
              <span className="font-bold text-red-600">
                {procedure.numRef
                  ? procedure.numRef
                  : procedure.internCode
                    ? procedure.internCode
                    : ""}
              </span>
            </H2>
            {procedure.category === "multiple" && (
              <Button onClick={listSubProcedure} className="py-1 px-3">
                <i className="fas fa-list-ol"></i>
              </Button>
            )}
          </div>
          {(() => {
            // 1. Sort and filter fields
            let preFields = data.fields
              .sort((a, b) => a.order - b.order)
              .filter(
                (value) =>
                  value.name !==
                    "seleccionarUsuarioQueVaAGenerarSolicitudDeCompraTr4" &&
                  value.name !== "requiereTipoDeCambioBs/Eur",
              );

            // 2. Handle datosGeneralesDeTransporte container
            const generalDataField = preFields.find(
              (f) => f.name === "datosGeneralesDeTransporte",
            );

            let fieldsForForm;
            if (generalDataField) {
              const subFieldNames = preFields
                .filter(
                  (f) =>
                    f.name !== "datosGeneralesDeTransporte" &&
                    f.name !== "subastaDeTransporte",
                )
                .map((f) => f.name);

              generalDataField.fieldsList = preFields.filter((f) =>
                subFieldNames.includes(f.name),
              );

              fieldsForForm = preFields.filter(
                (f) =>
                  f.name === "datosGeneralesDeTransporte" ||
                  f.name === "subastaDeTransporte",
              );
            } else {
              fieldsForForm = preFields;
            }

            // 3. Map to FormDefault format
            const formFields = fieldsForForm.map((value, index) => ({
              ...value,
              id: procedure.id,
              fieldId: value.id,
              label: value.label,
              labelCustom: (
                <>
                  <span className="text-green-600">{index + 1}.</span>{" "}
                  {value.label}
                </>
              ),
              name: value.name,
              validate: value.validate,
              lockEdition: validateArray(rols)
                ? !rols.find((item) => item.id == Number(rolId))
                : value.lockEdition,
              placeholder: "Complete el campo",
              type: value.type === "fileRead" ? "file" : value.type,
              typeInput: value.type === "fileRead" ? "file" : value.type,
              initial: value.initial ? value.initial : null,
              urlApi: value.url
                ? value.url.includes("/")
                  ? value.url
                  : `/Dropdown/${value.url}/options`
                : "",
              urlInitial: value.url,
              value: "name",
              labelOption: "name",
              action: () => handleExcelModal(value),
              reload: handleReaload,
            }));

            // 4. Override typeInput for datosGeneralesDeTransporte
            if (generalDataField) {
              const dgField = formFields.find(
                (f) => f.name === "datosGeneralesDeTransporte",
              );
              if (dgField) {
                dgField.typeInput = "generalTransportData";
                dgField.type = "generalTransportData";
                dgField.validate = false;
              }
            }

            // 5. Insurance field customizations
            formFields.forEach((f) => {
              if (f.name === PURCHASE_ORDER_EUR_EXCHANGE_RATE_FIELD) {
                f.type = "number";
                f.typeInput = "number";
                f.conditionType = "custom";
                f.condition = (values) =>
                  isPurchaseOrderEurCurrency(values.moneda);
                f.placeholder = "Tipo de cambio Bs/EUR";
                f.children = (
                  <span className="text-xs text-orange-700 mt-1">
                    Use 0 si la OC ya está en EUR. Use un valor mayor a 0 para convertir Bs a EUR.
                  </span>
                );
              }

              if (
                f.name === TRAVEL_FROM_FIELD ||
                f.name === TRANSPORT_MODE_FIELD
              ) {
                f.typeInput =
                  f.name === TRAVEL_FROM_FIELD
                    ? "selectNewOption"
                    : "selectGet";
                f.type =
                  f.name === TRAVEL_FROM_FIELD
                    ? "selectNewOption"
                    : "selectGet";
                f.urlApi =
                  f.name === TRAVEL_FROM_FIELD
                    ? `/Dropdown/${TRAVEL_FROM_DROPDOWN}/options`
                    : `/Dropdown/${TRANSPORT_MODE_DROPDOWN}/options`;
                f.urlInitial =
                  f.name === TRAVEL_FROM_FIELD
                    ? TRAVEL_FROM_DROPDOWN
                    : TRANSPORT_MODE_DROPDOWN;
                f.placeholder =
                  f.name === TRAVEL_FROM_FIELD
                    ? "Seleccione travesía desde"
                    : "Seleccione medio de transporte";
              }
              if (f.name === TRAVEL_TO_FIELD) {
                f.placeholder = FIXED_TRAVEL_TO;
              }
              if (f.name === "polizaNro") {
                f.placeholder = FIXED_POLICY_NUMBER;
              }
              if (
                f.name === INSURANCE_RATE_FIELD &&
                !isEmptyValue(suggestedInsuranceRate)
              ) {
                f.children = (
                  <span className="text-xs text-blue-700 mt-1">
                    Tasa sugerida desde clasificación de seguro:{" "}
                    {suggestedInsuranceRate}%
                  </span>
                );
              }
              if (f.name === INSURANCE_EUR_USD_RATE_FIELD) {
                f.children = (
                  <span
                    className={
                      purchaseOrderCurrency === "EUR"
                        ? "text-xs text-orange-700 mt-1"
                        : "text-xs text-gray-500 mt-1"
                    }
                  >
                    {purchaseOrderCurrency === "EUR"
                      ? "Obligatorio: se usará para el costo seguro en USD."
                      : "No requerido: la Orden de Compra está registrada en USD."}
                  </span>
                );
              }
            });

            return (
              <FormDefault
                fields={formFields}
                initialValues={dataIntialValues(fieldsForForm)}
                validationSchema={Yup.object().shape(dataFields(fieldsForForm))}
                onSubmit={async (values, resetForm) =>
                  await onSubmit(values, resetForm, fieldsForForm)
                }
                buttonFixed={true}
                buttonSecondary={true}
                buttonName="Registrar"
              />
            );
          })()}
        </>
      ) : (
        <H3>NO SE TIENE NINGÚN FORMULARIO</H3>
      )}
    </>
  );
};

const ListSubProcedure = (props) => {
  const { procedure, reload, onClose, rolId, step, typeId, openModal } = props;
  const { openDialog, dialogClose } = useDialog();
  const listProcedureId = procedure.id || procedure.procedureId;
  const { data, loading, call } = Request({
    urlApi: `/procedure/${listProcedureId}/sub-procedures`,
  });

  function createSubProcedure() {
    openDialog(
      <DialogConfirmation
        children={<H3>¿Estas seguro que quieres adicionar un sub tramite?</H3>}
        onClose={dialogClose}
        method="post"
        url={`/Procedure/${listProcedureId}/sub-procedures`}
        handleSuccess={call}
        texBtn="Adicionar"
      />,
    );
  }
  function formProcedure() {
    openModal(<FillForm {...props} />);
  }
  function onClick(item) {
    openDialog(
      <FillForm
        {...props}
        reload={call}
        procedure={item}
        onClose={dialogClose}
      />,
    );
  }

  function handleFormQuotes(item) {
    openModal(<FormQuotes procedure={item} />);
  }
  function handleTransportContract(item) {
    openModal(<FormTransportContract procedure={item} />);
  }
  function handleArriboImportacion(item) {
    openModal(<FormArriboImportacion procedure={item} />);
  }
  function handleNacionalizacion(item) {
    openModal(<FormNacionalizacion procedure={item} />);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1">
          <Button
            onClick={formProcedure}
            className="py-0.5 px-2 bg-[#FBCE00] text-[#000]"
          >
            <i className="fas fa-chevron-left"></i>
          </Button>
          <H2>Lista de sub procedimientos</H2>
        </div>
        <Button onClick={createSubProcedure} className="py-1 px-3">
          <i className="fas fa-plus"></i>
        </Button>
      </div>
      {!loading ? (
        <Circle />
      ) : data.length > 0 ? (
        <div className="w-full">
          <TableComplete
            header={[
              { name: "id", label: "Trámite" },
              { name: "internCode", label: "Nro. interno" },
              { name: "dimNumber", label: "Nro. DIM/DEX" },
              {
                name: "Action",
                label: "Acciones",
                type: "action",
                sticky: true,
                actions: [
                  {
                    label: "Bitácora",
                    icon: "fas fa-book",
                    action: handleFormQuotes,
                    color: "text-[#3b82f6]",
                  },
                  {
                    label: "Formulario de contrato de transporte",
                    icon: "fa-solid fa-file-contract",
                    action: handleTransportContract,
                    color: "text-[#f59e0b]",
                  },
                  {
                    label: "Transito",
                    icon: "fa-solid fa-ship",
                    action: handleArriboImportacion,
                    color: "text-[#f59e0b]",
                  },
                  {
                    label: "Nacionalización",
                    icon: "fa-solid fa-file-contract",
                    action: handleNacionalizacion,
                    color: "text-[#10b981]",
                  },
                ],
              },
            ]}
            data={data}
            onClick={onClick}
          />
        </div>
      ) : (
        <div className="">No hay tramites</div>
      )}
    </div>
  );
};

export default FillForm;
